import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { getPortalPageEditing } from 'src/utils/portal-page-editing'
import { withStyles } from '@material-ui/core'
import styles from './styles'
import { useSelector } from 'react-redux'
import { HeaderSettingsTheme, HeaderSettingsType } from './components'
import {
  formatPrice,
  getPaymentFrequency,
} from 'src/components/editor/components/custom-plugins/simple-box-wrapper-options/utils'
import { editorEnum } from 'src/model/editor/editorEnum'
import { headerBackgroundCss } from 'src/utils/default-header-background'
const HeaderSettings = props => {
  const { classes, onSave } = props
  const id = window.location.pathname.split('/')[2]
  const pageUrl = window.location.pathname.split('/')[1]
  const courseSettings = useSelector(state => state.course.landingPageSettings)
  const collectionSettings = useSelector(
    state => state.collection.landingPageSettings,
  )
  const collectionGeneralSettings = useSelector(
    state => state.collection.collectionSettings,
  )
  const collection = useSelector(state => state.collection.collection)
  const course = useSelector(state => state.course.course)

  const landingPageData =
    pageUrl === 'mini-course' ? courseSettings : collectionSettings
  const landingPageSection = landingPageData?.sections?.find(
    x => x.type === editorEnum.headerType,
  )
  // Initialize theme settings properly
  const initialPosition = landingPageSection?.content?.alignment || 'center'
  const initialBackground = landingPageSection?.content?.image || ''
  const initialType = landingPageSection?.content?.type || 1
  const initialColorProperties = {
    backgroundColor: landingPageSection?.content?.backgroundColor || '#FFFFFF',
    textColor: landingPageSection?.content?.textColor || '#002E47',
  }
  const initialDescription = landingPageSection?.content?.description || ''
  const initialPriceTagActive =
    landingPageSection?.content?.priceTagActive || false
  const initialPriceValue = landingPageSection?.content?.priceValue || ''
  const initialSelectedCurrency = landingPageSection?.content
    ?.selectedCurrency || {
    symbol: '$',
    code: 'USD',
    name: '$ (USD)',
  }
  const initialThumbnailImage =
    landingPageSection?.content?.thumbnailImage ||
    'https://cdn.minicoursegenerator.com/assets/editor/header-card-thumbnail.jpg'
  const initialFeatures = landingPageSection?.content?.features || [
    {
      id: 1,
      text: 'Feature one',
      checked: true,
      icon: 'material-symbols:check-rounded',
    },
    {
      id: 2,
      text: 'Feature two',
      checked: true,
      icon: 'material-symbols:check-rounded',
    },
    {
      id: 3,
      text: 'Feature three',
      checked: true,
      icon: 'material-symbols:check-rounded',
    },
    {
      id: 4,
      text: 'Feature four',
      checked: true,
      icon: 'material-symbols:check-rounded',
    },
  ]

  const [themeSettings, setThemeSettings] = useState({
    background: initialBackground,
    selectedPosition: initialPosition,
    selectedType: initialType,
    colorProperties: initialColorProperties,
    description: initialDescription,
    priceTagActive: initialPriceTagActive,
    priceValue: initialPriceValue,
    selectedCurrency: initialSelectedCurrency,
    thumbnailImage: initialThumbnailImage,
    features: initialFeatures,
  })

  // Store initial button text values for comparison
  const initialButtonValues = useMemo(
    () => ({
      startButton:
        landingPageData?.localizations?.startButton || 'Start Course',
      resumeButton:
        landingPageData?.localizations?.resumeButton || 'Resume Course',
      readMore: landingPageData?.localizations?.readMore || 'See more',
    }),
    [landingPageData?.localizations],
  )

  // Add state for button text values
  const [selectedStartValue, setSelectedStartValue] = useState(
    initialButtonValues.startButton,
  )
  const [selectedResumeValue, setSelectedResumeValue] = useState(
    initialButtonValues.resumeButton,
  )
  const [selectedReadMoreValue, setSelectedReadMoreValue] = useState(
    initialButtonValues.readMore,
  )

  // Update state when landingPageData changes (after save)
  useEffect(() => {
    setSelectedStartValue(
      landingPageData?.localizations?.startButton ||
        (pageUrl === 'mini-course' ? 'Start Course' : 'Get Started'),
    )
    setSelectedResumeValue(
      landingPageData?.localizations?.resumeButton || 'Resume Course',
    )
    setSelectedReadMoreValue(
      landingPageData?.localizations?.readMore || 'See more',
    )
  }, [landingPageData?.localizations, pageUrl])

  // Replace useTabs with simple activeTab state

  // Fix: Create the tabList properly to exclude Language tab for non-mini-course pages

  // Helper function to check if localizations have changed
  const hasLocalizationsChanged = useCallback(() => {
    return (
      selectedStartValue !== initialButtonValues.startButton ||
      selectedResumeValue !== initialButtonValues.resumeButton ||
      selectedReadMoreValue !== initialButtonValues.readMore
    )
  }, [
    selectedStartValue,
    selectedResumeValue,
    selectedReadMoreValue,
    initialButtonValues,
  ])

  // Create a function to update the onSave callback with latest values
  const updateSaveCallback = useCallback(
    (updatedData = {}) => {
      if (onSave && typeof onSave === 'function') {
        // Get the current content to preserve existing values
        const currentContent = landingPageSection?.content || {}

        // If colorProperties exists in themeSettings, merge them into the content
        let finalContent = {
          ...currentContent,
          ...updatedData,
        }

        if (themeSettings.colorProperties) {
          finalContent.backgroundColor =
            themeSettings.colorProperties.backgroundColor
          finalContent.textColor = themeSettings.colorProperties.textColor
        }

        // Also merge other properties from themeSettings if they exist
        if (themeSettings.selectedPosition !== undefined) {
          finalContent.alignment = themeSettings.selectedPosition
        }
        if (themeSettings.background !== undefined) {
          finalContent.image = themeSettings.background
        }
        if (themeSettings.selectedType !== undefined) {
          finalContent.type = themeSettings.selectedType
        }
        if (themeSettings.description !== undefined) {
          finalContent.description = themeSettings.description
        }
        if (themeSettings.priceTagActive !== undefined) {
          finalContent.priceTagActive = themeSettings.priceTagActive
        }
        if (themeSettings.priceValue !== undefined) {
          finalContent.priceValue = themeSettings.priceValue
        }
        if (themeSettings.selectedCurrency) {
          finalContent.selectedCurrency = themeSettings.selectedCurrency
        }
        if (themeSettings.thumbnailImage !== undefined) {
          finalContent.thumbnailImage = themeSettings.thumbnailImage
        }
        if (themeSettings.features !== undefined) {
          finalContent.features = themeSettings.features
        }

        const saveData = {
          ...themeSettings,
          id: id,
          landingPageSettings: {
            ...landingPageData,
            sections: [
              {
                ...landingPageSection,
                content: finalContent,
              },
              ...(landingPageData?.sections?.slice(1) || []),
            ],
          },
        }

        // Only include localizations if they have changed and view type is not 1 (DEFAULT)
        const currentViewType = landingPageSection?.content?.type || 1
        if (hasLocalizationsChanged() && currentViewType !== 1) {
          saveData.landingPageSettings.localizations = {
            ...(landingPageData?.localizations || {}),
            startButton: selectedStartValue,
            resumeButton: selectedResumeValue,
            readMore: selectedReadMoreValue,
            ...updatedData?.localizations,
          }
          // Also include button text values at top level for the Save button handler
          saveData.startButton = selectedStartValue
          saveData.resumeButton = selectedResumeValue
          saveData.readMore = selectedReadMoreValue
        }

        onSave(saveData)
      }
    },
    [
      onSave,
      themeSettings,
      id,
      landingPageData,
      selectedStartValue,
      selectedResumeValue,
      selectedReadMoreValue,
      hasLocalizationsChanged,
    ],
  )

  // Don't call updateSaveCallback when button values change
  // Button text changes should only be saved when the Save button is clicked
  // useEffect(() => {
  //   updateSaveCallback()
  // }, [
  //   selectedStartValue,
  //   selectedResumeValue,
  //   selectedReadMoreValue,
  //   updateSaveCallback,
  // ])

  // Watch for primary color changes and update price tag icons
  useEffect(() => {
    const primaryColor = landingPageData?.primaryColor || '#015FFB'

    // Update price tag icon colors
    document
      .querySelectorAll('.course-container-header-price svg path')
      .forEach(path => {
        path.setAttribute('fill', primaryColor)
      })
  }, [landingPageData?.primaryColor])

  // Watch for collection general settings changes (price updates from GeneralSettings modal)
  useEffect(() => {
    const hasPrice =
      collectionGeneralSettings?.price &&
      collectionGeneralSettings?.accessRule == 4
    const primaryColor = landingPageData?.primaryColor || '#015FFB'

    // Update price tag content when collection settings change
    document
      .querySelectorAll('.course-container-header-price')
      .forEach(element => {
        const pElement = element.querySelector('p')
        if (pElement && element.style.display !== 'none') {
          // Create SVG with primary color
          const svgHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M17.4703 7.5803L11.0503 1.1703C10.8868 1.00581 10.6711 0.903291 10.4403 0.880297L1.0903 0.000296701C0.943829 -0.0131743 0.796192 0.00583103 0.657911 0.0559579C0.51963 0.106085 0.39411 0.186099 0.290297 0.290297C0.186099 0.39411 0.106085 0.51963 0.0559579 0.657911C0.00583103 0.796192 -0.0131743 0.943829 0.000296701 1.0903L0.880297 10.4403C0.903291 10.6711 1.00581 10.8868 1.1703 11.0503L7.5803 17.4703C7.92409 17.8094 8.38741 17.9997 8.8703 18.0003C9.35015 17.9993 9.81018 17.8088 10.1503 17.4703L17.4703 10.1503C17.8104 9.80914 18.0014 9.34704 18.0014 8.8653C18.0014 8.38355 17.8104 7.92145 17.4703 7.5803ZM7.5603 7.5803C7.35059 7.79026 7.08332 7.93331 6.7923 7.99134C6.50128 8.04937 6.19958 8.01977 5.92538 7.90629C5.65118 7.79282 5.41681 7.60056 5.2519 7.35385C5.087 7.10713 4.99898 6.81705 4.99898 6.5203C4.99898 6.22355 5.087 5.93346 5.2519 5.68675C5.41681 5.44003 5.65118 5.24778 5.92538 5.1343C6.19958 5.02082 6.50128 4.99123 6.7923 5.04926C7.08332 5.10728 7.35059 5.25033 7.5603 5.4603C7.83754 5.73943 7.99313 6.11688 7.99313 6.5103C7.99313 6.90371 7.83754 7.28116 7.5603 7.5603V7.5803Z" fill="${primaryColor}"/>
          </svg>`

          if (hasPrice) {
            const formattedPrice = formatPrice(collectionGeneralSettings.price)
            const paymentFrequency = getPaymentFrequency(
              collectionGeneralSettings.price,
            ).label
            pElement.innerHTML = `${svgHTML} ${formattedPrice}<span class="course-container-price-title">${paymentFrequency}</span>`
          } else {
            pElement.innerHTML = `${svgHTML} Free`
          }
        }
      })
  }, [
    collectionGeneralSettings?.price,
    collectionGeneralSettings?.accessRule,
    landingPageData?.primaryColor,
  ])

  // Watch for collection/course title changes and update DOM in real-time
  useEffect(() => {
    // A shop landing page (Catalog, About…) shows its own name.
    const portalPage = getPortalPageEditing()
    const title = portalPage
      ? portalPage.name
      : pageUrl === 'mini-course'
      ? course?.name || 'Course Title'
      : collection?.name || 'Learning Path Title'

    // Update title in all template types
    // Hero template
    document.querySelectorAll('.hero-title').forEach(element => {
      element.textContent = title
    })

    // Promo template
    document.querySelectorAll('.promo-title').forEach(element => {
      element.textContent = title
    })

    // Default template
    document
      .querySelectorAll('.course-container-header-text')
      .forEach(element => {
        element.textContent = title
      })
  }, [collection?.name, course?.name, pageUrl])

  // Fixed handleThemeSettingsChange function
  const handleThemeSettingsChange = (settingName, value) => {
    // Create updated theme settings
    const updatedSettings = {
      ...themeSettings,
      [settingName]: value,
    }

    // Update the local state first
    setThemeSettings(updatedSettings)

    // Update DOM elements in real-time for immediate preview
    if (settingName === 'description') {
      // Update description in hero template
      document.querySelectorAll('.hero-description').forEach(element => {
        element.textContent = value
        // Show/hide based on whether value is empty
        element.style.display = value ? '-webkit-box' : 'none'
      })
      // Update description in promo template
      document.querySelectorAll('.promo-description').forEach(element => {
        element.textContent = value
        // Show/hide based on whether value is empty
        element.style.display = value ? '-webkit-box' : 'none'
      })
    } else if (settingName === 'background') {
      // Update background image in real-time
      // For Hero template (type 2), update the hero-image-container
      if (themeSettings.selectedType === 2) {
        document.querySelectorAll('.hero-image-container').forEach(element => {
          element.style.background = headerBackgroundCss(
            value,
            0.02,
            landingPageData?.primaryColor,
          )
        })
      } else {
        // For other templates, update the editor-header-container
        document
          .querySelectorAll('.editor-header-container')
          .forEach(element => {
            element.style.background = headerBackgroundCss(
              value,
              0.3,
              landingPageData?.primaryColor,
            )
          })
      }
    } else if (settingName === 'colorProperties') {
      // Update colors in real-time
      if (value.backgroundColor) {
        document
          .querySelectorAll('.editor-header-container')
          .forEach(element => {
            element.style.backgroundColor = value.backgroundColor
          })
      }
      if (value.textColor) {
        document
          .querySelectorAll(
            '.hero-title, .hero-description, .promo-title, .promo-description',
          )
          .forEach(element => {
            element.style.color = value.textColor
          })

        // Keep feature text always #002E47
        document.querySelectorAll('.promo-feature-text').forEach(element => {
          element.style.color = '#002E47'
        })
      }
    } else if (settingName === 'selectedPosition') {
      // Update alignment in real-time for default template
      const positionMap = {
        left: 'flex-start',
        center: 'center',
        right: 'flex-end',
      }
      document.querySelectorAll('.editor-header-content').forEach(element => {
        element.style.alignItems = positionMap[value]
        element.style.textAlign = value
      })

      // Update alignment in real-time for hero template
      const heroFlexDirection = value === 'left' ? 'row' : 'row-reverse'
      document.querySelectorAll('.hero-main-content').forEach(element => {
        element.style.flexDirection = heroFlexDirection
      })
    } else if (settingName === 'startButton') {
      // Update state but don't update DOM in real-time
      setSelectedStartValue(value)
      // Continue to save logic below but skip DOM manipulation
    } else if (settingName === 'resumeButton') {
      // Update state but don't update DOM in real-time
      setSelectedResumeValue(value)
      // Continue to save logic below but skip DOM manipulation
    } else if (settingName === 'readMore') {
      // Update state but don't update DOM in real-time
      setSelectedReadMoreValue(value)
      // Continue to save logic below but skip DOM manipulation
    } else if (settingName === 'priceTagActive') {
      // Update price tag visibility and content in real-time
      const hasPrice =
        collectionGeneralSettings?.price &&
        collectionGeneralSettings?.accessRule == 4
      const primaryColor = landingPageData?.primaryColor || '#015FFB'

      document
        .querySelectorAll('.course-container-header-price')
        .forEach(element => {
          element.style.display = value ? 'block' : 'none'

          // Update the price content
          if (value) {
            const pElement = element.querySelector('p')
            if (pElement) {
              // Create SVG with primary color
              const svgHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M17.4703 7.5803L11.0503 1.1703C10.8868 1.00581 10.6711 0.903291 10.4403 0.880297L1.0903 0.000296701C0.943829 -0.0131743 0.796192 0.00583103 0.657911 0.0559579C0.51963 0.106085 0.39411 0.186099 0.290297 0.290297C0.186099 0.39411 0.106085 0.51963 0.0559579 0.657911C0.00583103 0.796192 -0.0131743 0.943829 0.000296701 1.0903L0.880297 10.4403C0.903291 10.6711 1.00581 10.8868 1.1703 11.0503L7.5803 17.4703C7.92409 17.8094 8.38741 17.9997 8.8703 18.0003C9.35015 17.9993 9.81018 17.8088 10.1503 17.4703L17.4703 10.1503C17.8104 9.80914 18.0014 9.34704 18.0014 8.8653C18.0014 8.38355 17.8104 7.92145 17.4703 7.5803ZM7.5603 7.5803C7.35059 7.79026 7.08332 7.93331 6.7923 7.99134C6.50128 8.04937 6.19958 8.01977 5.92538 7.90629C5.65118 7.79282 5.41681 7.60056 5.2519 7.35385C5.087 7.10713 4.99898 6.81705 4.99898 6.5203C4.99898 6.22355 5.087 5.93346 5.2519 5.68675C5.41681 5.44003 5.65118 5.24778 5.92538 5.1343C6.19958 5.02082 6.50128 4.99123 6.7923 5.04926C7.08332 5.10728 7.35059 5.25033 7.5603 5.4603C7.83754 5.73943 7.99313 6.11688 7.99313 6.5103C7.99313 6.90371 7.83754 7.28116 7.5603 7.5603V7.5803Z" fill="${primaryColor}"/>
              </svg>`

              if (hasPrice) {
                const formattedPrice = formatPrice(
                  collectionGeneralSettings.price,
                )
                const paymentFrequency = getPaymentFrequency(
                  collectionGeneralSettings.price,
                ).label
                pElement.innerHTML = `${svgHTML} ${formattedPrice}<span class="course-container-price-title">${paymentFrequency}</span>`
              } else {
                pElement.innerHTML = `${svgHTML} Free`
              }
            }
          }
        })

      // Update price divider visibility
      document.querySelectorAll('.price-divider').forEach(element => {
        element.style.display = value ? 'block' : 'none'
      })
    } else if (
      settingName === 'priceValue' ||
      settingName === 'selectedCurrency'
    ) {
      // Update price value in real-time when price or currency changes
      const hasPrice =
        collectionGeneralSettings?.price &&
        collectionGeneralSettings?.accessRule == 4
      const primaryColor = landingPageData?.primaryColor || '#015FFB'

      document
        .querySelectorAll('.course-container-header-price')
        .forEach(element => {
          const pElement = element.querySelector('p')
          if (pElement) {
            // Create SVG with primary color
            const svgHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M17.4703 7.5803L11.0503 1.1703C10.8868 1.00581 10.6711 0.903291 10.4403 0.880297L1.0903 0.000296701C0.943829 -0.0131743 0.796192 0.00583103 0.657911 0.0559579C0.51963 0.106085 0.39411 0.186099 0.290297 0.290297C0.186099 0.39411 0.106085 0.51963 0.0559579 0.657911C0.00583103 0.796192 -0.0131743 0.943829 0.000296701 1.0903L0.880297 10.4403C0.903291 10.6711 1.00581 10.8868 1.1703 11.0503L7.5803 17.4703C7.92409 17.8094 8.38741 17.9997 8.8703 18.0003C9.35015 17.9993 9.81018 17.8088 10.1503 17.4703L17.4703 10.1503C17.8104 9.80914 18.0014 9.34704 18.0014 8.8653C18.0014 8.38355 17.8104 7.92145 17.4703 7.5803ZM7.5603 7.5803C7.35059 7.79026 7.08332 7.93331 6.7923 7.99134C6.50128 8.04937 6.19958 8.01977 5.92538 7.90629C5.65118 7.79282 5.41681 7.60056 5.2519 7.35385C5.087 7.10713 4.99898 6.81705 4.99898 6.5203C4.99898 6.22355 5.087 5.93346 5.2519 5.68675C5.41681 5.44003 5.65118 5.24778 5.92538 5.1343C6.19958 5.02082 6.50128 4.99123 6.7923 5.04926C7.08332 5.10728 7.35059 5.25033 7.5603 5.4603C7.83754 5.73943 7.99313 6.11688 7.99313 6.5103C7.99313 6.90371 7.83754 7.28116 7.5603 7.5603V7.5803Z" fill="${primaryColor}"/>
            </svg>`

            if (hasPrice) {
              const formattedPrice = formatPrice(
                collectionGeneralSettings.price,
              )
              const paymentFrequency = getPaymentFrequency(
                collectionGeneralSettings.price,
              ).label
              pElement.innerHTML = `${svgHTML} ${formattedPrice}<span class="course-container-price-title">${paymentFrequency}</span>`
            } else {
              pElement.innerHTML = `${svgHTML} Free`
            }
          }
        })
    }

    // Prepare data for the onSave callback
    const updatedData = {}

    // Get the current content to preserve existing values
    const currentContent = landingPageSection?.content || {}

    // If the changed setting is selectedPosition, add it to the updated data as alignment
    if (settingName === 'selectedPosition') {
      updatedData.alignment = value
    }

    // If the changed setting is background, add it to the updated data as image
    if (settingName === 'background') {
      updatedData.image = value
    }

    // If the changed setting is selectedType, add it to the updated data as type
    if (settingName === 'selectedType') {
      updatedData.type = value
    }

    // If the changed setting is description, add it to the updated data as description
    if (settingName === 'description') {
      updatedData.description = value
    }

    // If the changed setting is colorProperties, add individual color properties
    if (settingName === 'colorProperties') {
      updatedData.backgroundColor = value.backgroundColor
      updatedData.textColor = value.textColor
    }

    // If the changed setting is priceTagActive, add it to the updated data
    if (settingName === 'priceTagActive') {
      updatedData.priceTagActive = value
    }

    // If the changed setting is priceValue, add it to the updated data
    if (settingName === 'priceValue') {
      updatedData.priceValue = value
    }

    // If the changed setting is selectedCurrency, add it to the updated data
    if (settingName === 'selectedCurrency') {
      updatedData.selectedCurrency = value
    }

    // If the changed setting is thumbnailImage, add it to the updated data
    if (settingName === 'thumbnailImage') {
      updatedData.thumbnailImage = value
    }

    // If the changed setting is features, add it to the updated data
    if (settingName === 'features') {
      updatedData.features = value
    }

    // Call onSave with the updated settings
    if (onSave && typeof onSave === 'function') {
      // If colorProperties exists in updatedSettings, merge them into the content
      let finalContent = {
        ...currentContent,
        ...updatedData,
      }

      if (updatedSettings.colorProperties) {
        finalContent.backgroundColor =
          updatedSettings.colorProperties.backgroundColor
        finalContent.textColor = updatedSettings.colorProperties.textColor
      }

      // Also merge other properties from themeSettings if they exist
      if (updatedSettings.selectedPosition !== undefined) {
        finalContent.alignment = updatedSettings.selectedPosition
      }
      if (updatedSettings.background !== undefined) {
        finalContent.image = updatedSettings.background
      }
      if (updatedSettings.selectedType !== undefined) {
        finalContent.type = updatedSettings.selectedType
      }
      if (updatedSettings.description !== undefined) {
        finalContent.description = updatedSettings.description
      }
      if (updatedSettings.priceTagActive !== undefined) {
        finalContent.priceTagActive = updatedSettings.priceTagActive
      }
      if (updatedSettings.priceValue !== undefined) {
        finalContent.priceValue = updatedSettings.priceValue
      }
      if (updatedSettings.selectedCurrency) {
        finalContent.selectedCurrency = updatedSettings.selectedCurrency
      }
      if (updatedSettings.thumbnailImage !== undefined) {
        finalContent.thumbnailImage = updatedSettings.thumbnailImage
      }
      if (updatedSettings.features !== undefined) {
        finalContent.features = updatedSettings.features
      }
      const landingPageSectionMain = landingPageData?.sections?.find(
        x => x.type === editorEnum.mainType,
      )

      const saveData = {
        ...updatedSettings,
        id: id,
        landingPageSettings: {
          ...landingPageData,
          sections: [
            {
              ...landingPageSection,
              content: finalContent,
            },
            {
              ...landingPageSectionMain,
              content: {
                ...landingPageSectionMain.content,
                html: window.editor.getData(),
              },
            },
            //...(landingPageData?.sections?.slice(1) || []),
          ],
        },
      }

      // Only include localizations if they have changed and view type is not 1 (DEFAULT)
      const currentViewType = landingPageSection?.content?.type || 1

      // Use latest values (from state or from current change)
      const latestStartValue =
        settingName === 'startButton' ? value : selectedStartValue
      const latestResumeValue =
        settingName === 'resumeButton' ? value : selectedResumeValue
      const latestReadMoreValue =
        settingName === 'readMore' ? value : selectedReadMoreValue

      // Check if any of the values differ from initial
      const hasChanged =
        latestStartValue !== initialButtonValues.startButton ||
        latestResumeValue !== initialButtonValues.resumeButton ||
        latestReadMoreValue !== initialButtonValues.readMore

      if (hasChanged && currentViewType !== 1) {
        saveData.landingPageSettings.localizations = {
          ...(landingPageData?.localizations || {}),
          startButton: latestStartValue,
          resumeButton: latestResumeValue,
          readMore: latestReadMoreValue,
        }
        // Also include button text values at top level for the Save button handler
        saveData.startButton = latestStartValue
        saveData.resumeButton = latestResumeValue
        saveData.readMore = latestReadMoreValue
      }

      onSave(saveData)
    }
  }

  /* const renderContent = () => {
    switch (activeTab) {
      case 'Theme':
        return (
          <HeaderSettingsTheme
            landingPageData={landingPageData}
            isLoading={isLoading}
            themeSettings={themeSettings}
            onThemeSettingsChange={handleThemeSettingsChange}
          />
        )
      case 'Type':
        return (
          <HeaderSettingsType
            landingPageData={landingPageData}
            isLoading={isLoading}
            themeSettings={themeSettings}
          />
        )
      case 'Language':
        return pageUrl === 'mini-course' ? (
          <HeaderSettingsLanguage
            landingPageData={landingPageData}
            isLoading={isLoading}
            themeSettings={themeSettings}
            selectedStartValue={selectedStartValue}
            selectedResumeValue={selectedResumeValue}
            onButtonTextChange={handleButtonTextChange}
          />
        ) : null
      default:
        return null
    }
  } */

  return (
    <div className={classes.container}>
      <div className={classes.content}>
        <HeaderSettingsType
          landingPageData={landingPageData}
          isLoading={false}
          themeSettings={themeSettings}
          onThemeSettingsChange={handleThemeSettingsChange}
        />

        <HeaderSettingsTheme
          landingPageData={landingPageData}
          isLoading={false}
          themeSettings={themeSettings}
          onThemeSettingsChange={handleThemeSettingsChange}
        />
      </div>
    </div>
  )
}

export default withStyles(styles)(HeaderSettings)
